/**
 * Level Check — running Z-delta along a horizontal beam.
 *
 * Workflow: pick or tag any two shots as START (datum) and END
 * (direction). Every shot then reports `along_m` (projected onto the
 * beam axis) and `z_delta_mm` (actual - design). The design profile
 * uses an optional slope (mm/m), the surveyor can input in mm/m or
 * degrees via a small toggle.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Crosshair, Download, X, Flag, MapPin } from "@phosphor-icons/react";
import { station, level as levelApi, target as targetApi, fmt } from "../lib/api";
import { ToolSplitLayout } from "../components/ToolSplitLayout";
import { AimPad } from "../components/AimPad";
import { PrintReportShell, PrintReportButton } from "../components/PrintReportShell";
import { useAuth } from "../lib/auth";
import { useLivePresence } from "../hooks/useLivePresence";

// ---------- Session-card in the list ---------------------------------

const SessionCard = ({ session, onOpen, onDelete }) => {
  const worst = Math.abs(session.max_z_delta_mm || 0);
  const readyPill = session.reference_ready
    ? "text-[#8DC63F]"
    : "text-[#FF9F0A]";
  const worstPill = worst === 0
    ? "text-[#8B939A]"
    : worst > session.tolerance_mm
      ? "text-[#FF3B30]"
      : "text-[#8DC63F]";
  return (
    <button
      data-testid={`level-session-${session.id}`}
      onClick={() => onOpen(session.id)}
      className="group relative flex w-full items-center justify-between gap-4 rounded-md border border-[#2A2E39] bg-[#14161A] px-4 py-3 text-left hover:border-[#009E8E]"
    >
      <div className="pointer-events-none absolute inset-y-1 left-1 w-1 rounded-sm bg-[#009E8E]/70" />
      <div className="pl-3">
        <div
          className="text-sm font-bold uppercase tracking-tight text-white"
          style={{ fontFamily: "Chakra Petch, sans-serif" }}
        >
          {session.name}
        </div>
        <div
          className="mt-0.5 text-[10px] uppercase tracking-widest text-[#8B939A]"
          style={{ fontFamily: "JetBrains Mono, monospace" }}
        >
          {session.n_shots} shots · {session.n_checks} checks ·{" "}
          <span className={worstPill}>worst {worst.toFixed(1)} mm</span> · tol ±
          {session.tolerance_mm.toFixed(1)} mm · slope{" "}
          {session.design_slope_mm_per_m.toFixed(2)} mm/m ·{" "}
          <span className={readyPill}>
            {session.reference_ready ? "axis set" : "axis pending"}
          </span>
        </div>
      </div>
      <button
        data-testid={`level-delete-${session.id}`}
        onClick={(e) => { e.stopPropagation(); onDelete(session.id); }}
        className="rounded-sm border border-transparent p-1 text-[#5A6065] opacity-0 group-hover:opacity-100 hover:border-[#FF3B30] hover:text-[#FF3B30]"
      >
        <X size={14} weight="bold" />
      </button>
    </button>
  );
};

// ---------- Elevation-profile visualisation ---------------------------

const ElevationView = ({ session }) => {
  const shots = session.shots || [];
  const ready = session.reference_ready;
  const tol = session.tolerance_mm;
  const slope = session.design_slope_mm_per_m;

  // Data ranges
  const alongs = shots.map((s) => s.along_m ?? 0);
  const zs = shots.map((s) => s.z_actual_mm ?? 0);
  const alongMin = Math.min(0, ...alongs);
  const alongMax = Math.max(1, ...alongs);
  const alongSpan = Math.max(0.5, alongMax - alongMin);
  const zSpan = Math.max(2 * tol + 4, Math.max(...zs.map(Math.abs), ...zs.map((z) => Math.abs(z - alongMax * slope))) * 2 + 4);
  const zLo = -zSpan / 2;
  const zHi = zSpan / 2;

  const W = 600;
  const H = 260;
  const padL = 44;
  const padR = 12;
  const padT = 16;
  const padB = 32;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const xFor = (a) => padL + ((a - alongMin) / alongSpan) * plotW;
  const yFor = (z) => padT + (1 - (z - zLo) / (zHi - zLo)) * plotH;

  // Design-line points (start=alongMin, end=alongMax) both projected
  // through z = along * slope (z_start = 0 in mm).
  const designLine = ready
    ? [
        { a: alongMin, z: alongMin * slope },
        { a: alongMax, z: alongMax * slope },
      ]
    : null;

  // Tolerance envelope shape
  const envelopePath = ready
    ? `M ${xFor(alongMin)} ${yFor(alongMin * slope + tol)} ` +
      `L ${xFor(alongMax)} ${yFor(alongMax * slope + tol)} ` +
      `L ${xFor(alongMax)} ${yFor(alongMax * slope - tol)} ` +
      `L ${xFor(alongMin)} ${yFor(alongMin * slope - tol)} Z`
    : null;

  return (
    <div
      data-testid="level-elevation-view"
      className="rounded-md border border-[#2A2E39] bg-[#14161A] p-3"
    >
      <div className="mb-2 flex items-center justify-between text-[10px] uppercase tracking-widest text-[#8B939A]">
        <span>Elevation profile</span>
        <span style={{ fontFamily: "JetBrains Mono, monospace" }}>
          along {(alongSpan * 1000).toFixed(0)} mm · ± {tol.toFixed(1)} mm
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" style={{ fontFamily: "JetBrains Mono, monospace" }}>
        {/* Grid + axes */}
        <rect x={padL} y={padT} width={plotW} height={plotH} fill="none" stroke="#1A1D24" />
        {[-1, -0.5, 0, 0.5, 1].map((f) => {
          const z = f * (zHi - zLo) / 2;
          return (
            <g key={f}>
              <line
                x1={padL} y1={yFor(z)}
                x2={padL + plotW} y2={yFor(z)}
                stroke="#1A1D24"
              />
              <text x={padL - 4} y={yFor(z) + 3} fill="#5A6065" fontSize="9" textAnchor="end">
                {z.toFixed(0)}
              </text>
            </g>
          );
        })}
        <text x={padL - 34} y={padT + plotH / 2} fill="#8B939A" fontSize="9" transform={`rotate(-90 ${padL - 34} ${padT + plotH / 2})`}>
          Δz (mm)
        </text>
        <text x={padL + plotW / 2} y={H - 8} fill="#8B939A" fontSize="9" textAnchor="middle">
          along (m)
        </text>

        {/* Tolerance envelope (only when axis is set) */}
        {envelopePath && (
          <path d={envelopePath} fill="#009E8E" fillOpacity="0.08" stroke="none" />
        )}
        {/* Design line */}
        {designLine && (
          <line
            x1={xFor(designLine[0].a)} y1={yFor(designLine[0].z)}
            x2={xFor(designLine[1].a)} y2={yFor(designLine[1].z)}
            stroke="#009E8E" strokeDasharray="4 3" strokeWidth="1.5"
          />
        )}

        {/* Shot markers */}
        {ready && shots.map((sh) => {
          if (sh.along_m == null || sh.z_actual_mm == null) return null;
          const cx = xFor(sh.along_m);
          const cy = yFor(sh.z_actual_mm);
          const isAnchor = sh.role === "start" || sh.role === "end";
          const color = isAnchor
            ? sh.role === "start" ? "#8DC63F" : "#F5A623"
            : sh.within_tol ? "#8DC63F" : "#FF3B30";
          // Stem from design line to actual z
          const yDesign = yFor(sh.along_m * slope);
          return (
            <g key={sh.id}>
              <line x1={cx} y1={yDesign} x2={cx} y2={cy} stroke={color} strokeOpacity="0.5" strokeWidth="1" />
              <circle cx={cx} cy={cy} r={isAnchor ? 5 : 4} fill={color} />
              {sh.label && (
                <text x={cx + 6} y={cy - 6} fill="#B5BEC5" fontSize="9">
                  {sh.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div className="mt-2 flex items-center gap-4 text-[9px] uppercase tracking-widest text-[#8B939A]">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-1.5 w-3 rounded-full bg-[#009E8E]" /> Design
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#8DC63F]" /> In tol / Start
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#F5A623]" /> End
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#FF3B30]" /> Out of tol
        </span>
      </div>
    </div>
  );
};

// ---------- Shot row --------------------------------------------------

const ShotRow = ({ shot, index, onDelete, onSetStart, onSetEnd, isStart, isEnd, isFlat }) => {
  const role = shot.role;
  const roleTone =
    role === "start" ? "bg-[#8DC63F]/20 text-[#8DC63F]"
    : role === "end" ? "bg-[#F5A623]/20 text-[#F5A623]"
    : role === "check"
      ? (shot.within_tol
          ? "bg-[#8DC63F]/10 text-[#8DC63F]"
          : "bg-[#FF3B30]/20 text-[#FF3B30]")
      : "bg-[#2A2E39] text-[#8B939A]";
  const roleLabel = role === "check"
    ? (shot.within_tol ? "PASS" : "FAIL")
    : role.toUpperCase();
  return (
    <tr data-testid={`level-shot-row-${shot.id}`} className="border-b border-[#1A1D24]">
      <td className="p-2 text-[#8B939A]">#{index + 1}</td>
      <td className="p-2 text-white">{shot.label || "—"}</td>
      {!isFlat && (
        <td className="p-2 text-right">{shot.along_m == null ? "—" : (shot.along_m * 1000).toFixed(0)}</td>
      )}
      <td className="p-2 text-right">{shot.z_actual_mm == null ? "—" : shot.z_actual_mm.toFixed(1)}</td>
      {!isFlat && (
        <td className="p-2 text-right">{shot.z_expected_mm == null ? "—" : shot.z_expected_mm.toFixed(1)}</td>
      )}
      <td className={`p-2 text-right ${shot.z_delta_mm != null && Math.abs(shot.z_delta_mm) > 0 && !shot.within_tol ? "text-[#FF3B30]" : "text-white"}`}>
        {shot.z_delta_mm == null ? "—" : (shot.z_delta_mm >= 0 ? "+" : "") + shot.z_delta_mm.toFixed(1)}
      </td>
      <td className="p-2 text-center">
        <span className={`rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${roleTone}`}>
          {roleLabel}
        </span>
      </td>
      <td className="p-2">
        <div className="flex items-center justify-end gap-1">
          {!isFlat && (
            <>
              <button
                data-testid={`level-set-start-${shot.id}`}
                onClick={() => onSetStart(shot.id)}
                title="Tag as START (datum)"
                className={`rounded-sm border p-1 text-[10px] uppercase tracking-widest transition-colors ${
                  isStart
                    ? "border-[#8DC63F] bg-[#8DC63F]/20 text-[#8DC63F]"
                    : "border-[#2A2E39] text-[#5A6065] hover:border-[#8DC63F] hover:text-[#8DC63F]"
                }`}
              >
                <Flag size={12} weight="bold" />
              </button>
              <button
                data-testid={`level-set-end-${shot.id}`}
                onClick={() => onSetEnd(shot.id)}
                title="Tag as END (direction)"
                className={`rounded-sm border p-1 text-[10px] uppercase tracking-widest transition-colors ${
                  isEnd
                    ? "border-[#F5A623] bg-[#F5A623]/20 text-[#F5A623]"
                    : "border-[#2A2E39] text-[#5A6065] hover:border-[#F5A623] hover:text-[#F5A623]"
                }`}
              >
                <MapPin size={12} weight="bold" />
              </button>
            </>
          )}
          <button
            data-testid={`level-shot-delete-${shot.id}`}
            onClick={() => onDelete(shot.id)}
            className="rounded-sm border border-transparent p-1 text-[#5A6065] hover:border-[#FF3B30] hover:text-[#FF3B30]"
          >
            <X size={12} weight="bold" />
          </button>
        </div>
      </td>
    </tr>
  );
};

// ---------- Stat card -------------------------------------------------

const Stat = ({ label, value, tone }) => {
  const color = tone === "ok" ? "#8DC63F" : tone === "warn" ? "#FF9F0A" : tone === "fail" ? "#FF3B30" : "#FFFFFF";
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.25em] text-[#8B939A]">{label}</div>
      <div className="mt-0.5 font-mono text-base" style={{ color }}>{value}</div>
    </div>
  );
};

// ---------- Slope input with mm/m ↔ deg toggle -----------------------

const slopeMMperMFromDeg = (deg) => Math.tan((deg * Math.PI) / 180) * 1000;
const slopeDegFromMMperM = (mm) => Math.atan(mm / 1000) * 180 / Math.PI;

const SlopeInput = ({ mmPerM, unit, onCommit }) => {
  const [raw, setRaw] = useState(
    unit === "deg" ? slopeDegFromMMperM(mmPerM).toFixed(3) : mmPerM.toFixed(2),
  );
  useEffect(() => {
    setRaw(unit === "deg" ? slopeDegFromMMperM(mmPerM).toFixed(3) : mmPerM.toFixed(2));
  }, [mmPerM, unit]);

  const commit = () => {
    const v = parseFloat(raw);
    if (!Number.isFinite(v)) return;
    const mm = unit === "deg" ? slopeMMperMFromDeg(v) : v;
    onCommit(mm, unit);
  };

  const flipUnit = () => {
    const nextUnit = unit === "deg" ? "mm_per_m" : "deg";
    // Persist same underlying slope, just change display unit.
    onCommit(mmPerM, nextUnit);
  };

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-[#8B939A]">Design slope</span>
        <button
          data-testid="level-slope-unit-toggle"
          onClick={flipUnit}
          className="rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-2 py-0.5 text-[9px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white"
        >
          {unit === "deg" ? "° → mm/m" : "mm/m → °"}
        </button>
      </div>
      <div className="flex items-center gap-2">
        <input
          data-testid="level-slope-input"
          type="number"
          step={unit === "deg" ? "0.001" : "0.01"}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
          className="w-full rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white focus:border-[#009E8E] focus:outline-none"
        />
        <span className="text-[10px] uppercase tracking-widest text-[#8B939A]">
          {unit === "deg" ? "°" : "mm/m"}
        </span>
      </div>
    </div>
  );
};

// ---------- Main page ------------------------------------------------

export default function LevelCheck({ onBack }) {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [newName, setNewName] = useState("");
  const [newTol, setNewTol] = useState("3.0");
  const [newSlope, setNewSlope] = useState("0");
  const [newMode, setNewMode] = useState("slope");
  const [tolInput, setTolInput] = useState("3.0");
  const [labelInput, setLabelInput] = useState("");
  const [measuring, setMeasuring] = useState(false);
  const [targetSetting, setTargetSetting] = useState(null);

  // Heartbeat for supervisor live-view
  useLivePresence({
    enabled: !!current?.id,
    tool: "level",
    sessionId: current?.id,
    sessionName: current?.name,
    username: user?.username,
    role: user?.role,
    counters: {
      shots: current?.shots?.length || 0,
      reference_ready: !!current?.reference_ready,
      max_delta_mm: current?.shots
        ? Math.max(0, ...current.shots.filter((s) => s.role === "check")
            .map((s) => Math.abs(s.z_delta_mm || 0)))
        : 0,
    },
  });

  const refreshSessions = useCallback(async () => {
    try { setSessions(await levelApi.listSessions()); } catch { toast.error("Could not load sessions"); }
  }, []);

  const refreshCurrent = useCallback(async (sid) => {
    try {
      const s = await levelApi.get(sid);
      setCurrent(s);
      setTolInput(String(s.tolerance_mm));
    } catch { setCurrent(null); }
  }, []);

  // Load active target for QA-stamp
  useEffect(() => {
    (async () => {
      try {
        const [s, ps] = await Promise.all([targetApi.get(), targetApi.presets()]);
        const active = s.preset_id === "custom"
          ? s.custom
          : (ps.presets || []).find((p) => p.id === s.preset_id);
        setTargetSetting(active || null);
      } catch { /* silent */ }
    })();
  }, [current?.id]);

  useEffect(() => { refreshSessions(); }, [refreshSessions]);

  const createSession = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Enter a beam name"); return; }
    const tol = parseFloat(newTol);
    if (!Number.isFinite(tol) || tol <= 0) { toast.error("Tolerance must be positive"); return; }
    const slope = parseFloat(newSlope) || 0;
    try {
      const s = await levelApi.create({
        name, tolerance_mm: tol,
        mode: newMode,
        design_slope_mm_per_m: newMode === "flat" ? 0 : slope,
        slope_display_unit: "mm_per_m",
      });
      setNewName("");
      await refreshSessions();
      refreshCurrent(s.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create session");
    }
  };

  const takeShot = async () => {
    if (!current) return;
    setMeasuring(true);
    try {
      const result = await station.measure("coord");
      if (result.x == null || result.y == null || result.z == null) {
        toast.error("No coord returned — is the TS16 aimed at the target?");
        return;
      }
      const updated = await levelApi.addShot(current.id, {
        label: labelInput.trim() || null,
        x: result.x, y: result.y, z: result.z,
        hz: result.hz, v: result.v, sd: result.sd,
        target_preset: targetSetting?.label || null,
        target_perpendicular_mm: targetSetting?.perpendicular_mm || 0,
      });
      setCurrent(updated);
      setLabelInput("");
      refreshSessions();
      const last = updated.shots[updated.shots.length - 1];
      if (last.role === "start") {
        toast.success("Start anchor captured — take END next");
      } else if (last.role === "end") {
        if (updated.warning) toast.error(updated.warning, { duration: 6000 });
        else toast.success(`Beam axis set · ${(updated.direction_length_m * 1000).toFixed(0)} mm — start checking`);
      } else if (last.role === "check") {
        toast[last.within_tol ? "success" : "error"](
          `Δz ${last.z_delta_mm >= 0 ? "+" : ""}${last.z_delta_mm.toFixed(1)} mm — ${last.within_tol ? "in tol" : "OUT OF TOL"}`,
        );
      } else {
        toast("Shot captured — tag it as start/end to fix the beam axis");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Measurement failed");
    } finally {
      setMeasuring(false);
    }
  };

  const deleteShot = async (shot_id) => {
    if (!current) return;
    try {
      const s = await levelApi.removeShot(current.id, shot_id);
      setCurrent(s); refreshSessions();
    } catch { toast.error("Delete failed"); }
  };

  const deleteSession = async (sid) => {
    try {
      await levelApi.remove(sid);
      refreshSessions();
      if (current?.id === sid) setCurrent(null);
    } catch { toast.error("Delete failed"); }
  };

  const saveTolerance = async () => {
    if (!current) return;
    const t = parseFloat(tolInput);
    if (!Number.isFinite(t) || t <= 0) return;
    try {
      const s = await levelApi.setTolerance(current.id, t);
      setCurrent(s); refreshSessions();
      toast.success(`Tolerance set to ±${t.toFixed(1)} mm`);
    } catch { toast.error("Could not update tolerance"); }
  };

  const saveSlope = async (mmPerM, unit) => {
    if (!current) return;
    try {
      const s = await levelApi.setSlope(current.id, mmPerM, unit);
      setCurrent(s); refreshSessions();
    } catch { toast.error("Could not update slope"); }
  };

  const setAnchor = async (which, shotId) => {
    if (!current) return;
    try {
      const payload =
        which === "start"
          ? { set_start: true, start_shot_id: shotId }
          : { set_end: true, end_shot_id: shotId };
      const s = await levelApi.setAnchors(current.id, payload);
      setCurrent(s); refreshSessions();
      toast.success(`${which === "start" ? "START" : "END"} → ${current.shots.find((sh) => sh.id === shotId)?.label || "shot"} `);
    } catch { toast.error("Could not update anchor"); }
  };

  // ---- List view ----

  if (!current) {
    return (
      <div className="mx-auto max-w-3xl p-6" data-testid="level-page-list">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B939A]">Level Check</div>
            <h2 className="text-2xl font-bold uppercase tracking-tight text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
              Beam Z-delta inspection
            </h2>
          </div>
          <button
            data-testid="level-back-list"
            onClick={onBack}
            className="rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-1.5 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white"
          >
            ← Toolbox
          </button>
        </div>

        {/* New session */}
        <div className="mb-6 rounded-md border border-[#2A2E39] bg-gradient-to-br from-[#1A1D24] to-[#14161A] p-4">
          <div className="mb-3 text-[10px] uppercase tracking-widest text-[#8B939A]">New session</div>
          <div className="mb-3 flex flex-wrap gap-2">
            <button
              data-testid="level-mode-slope"
              onClick={() => setNewMode("slope")}
              className={`rounded-sm border px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-all ${
                newMode === "slope"
                  ? "border-[#009E8E] bg-[#009E8E]/15 text-[#009E8E]"
                  : "border-[#2A2E39] bg-[#0A0B0E] text-[#8B939A] hover:border-[#009E8E]/50"
              }`}
              style={{ fontFamily: "Chakra Petch, sans-serif" }}
            >
              Slope inspection
            </button>
            <button
              data-testid="level-mode-flat"
              onClick={() => setNewMode("flat")}
              className={`rounded-sm border px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-all ${
                newMode === "flat"
                  ? "border-[#009E8E] bg-[#009E8E]/15 text-[#009E8E]"
                  : "border-[#2A2E39] bg-[#0A0B0E] text-[#8B939A] hover:border-[#009E8E]/50"
              }`}
              style={{ fontFamily: "Chakra Petch, sans-serif" }}
            >
              Flat surface
            </button>
          </div>
          <div className={`grid gap-3 ${newMode === "flat" ? "sm:grid-cols-[1fr,120px,140px]" : "sm:grid-cols-[1fr,120px,140px,140px]"}`}>
            <input
              data-testid="level-new-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={newMode === "flat" ? "Surface ID (e.g. Slab bay 3)" : "Beam ID (e.g. B3 mezz-floor)"}
              className="rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white placeholder:text-[#4A5054] focus:border-[#009E8E] focus:outline-none"
            />
            <div className="flex items-center gap-2">
              <input
                data-testid="level-new-tol"
                type="number" step="0.1"
                value={newTol}
                onChange={(e) => setNewTol(e.target.value)}
                className="w-full rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white focus:border-[#009E8E] focus:outline-none"
              />
              <span className="text-[10px] uppercase tracking-widest text-[#8B939A]">± mm</span>
            </div>
            {newMode !== "flat" && (
              <div className="flex items-center gap-2">
                <input
                  data-testid="level-new-slope"
                  type="number" step="0.1"
                  value={newSlope}
                  onChange={(e) => setNewSlope(e.target.value)}
                  placeholder="Slope"
                  className="w-full rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white focus:border-[#009E8E] focus:outline-none"
                />
                <span className="text-[10px] uppercase tracking-widest text-[#8B939A]">mm/m</span>
              </div>
            )}
            <button
              data-testid="level-create-btn"
              onClick={createSession}
              className="flex items-center justify-center gap-2 rounded-sm border border-[#009E8E] bg-[#009E8E]/10 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-[#009E8E] hover:bg-[#009E8E]/20"
              style={{ fontFamily: "Chakra Petch, sans-serif" }}
            >
              <Plus size={12} weight="bold" /> Create
            </button>
          </div>
          <div className="mt-2 text-[10px] text-[#5A6065]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
            {newMode === "flat"
              ? "Flat surface — take shots freely. Each row shows deviation from the average height. Typical tolerance: ±3 mm."
              : "Design slope 0 = perfectly level. Positive = beam rises going START → END. Typical steel-beam tolerance: ±3 mm."}
          </div>
        </div>

        <div className="mb-2 text-[10px] uppercase tracking-widest text-[#8B939A]">Sessions</div>
        {sessions.length === 0 ? (
          <div className="rounded-md border border-dashed border-[#2A2E39] p-6 text-center text-[11px] text-[#5A6065]">
            No sessions yet. Create one above to start checking a beam.
          </div>
        ) : (
          <div className="space-y-2">
            {sessions.map((s) => (
              <SessionCard
                key={s.id}
                session={s}
                onOpen={refreshCurrent}
                onDelete={deleteSession}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---- Session view ----

  const shots = current.shots || [];
  const nextIdx = shots.length;
  const isFlat = current.mode === "flat";
  const headline = isFlat
    ? current.reference_ready
      ? `${shots.length} shots · mean Z ${current.mean_z_m != null ? (current.mean_z_m * 1000).toFixed(1) + " mm" : "—"} · range ${current.range_mm?.toFixed(1) ?? "—"} mm`
      : "Take a shot to seed the mean"
    : current.reference_ready
      ? `${shots.filter((s) => s.role === "check").length} checks · axis ${(current.direction_length_m * 1000).toFixed(0)} mm @ ${current.design_slope_mm_per_m.toFixed(2)} mm/m`
      : shots.length === 0
        ? "Take START anchor"
        : shots.length === 1
          ? "Take END anchor"
          : "Tag START + END from the shot table";

  const startLabel = shots.find((s) => s.id === current.start_shot_id)?.label
    || (current.start_shot_id ? `Shot ${shots.findIndex((s) => s.id === current.start_shot_id) + 1}` : "—");
  const endLabel = shots.find((s) => s.id === current.end_shot_id)?.label
    || (current.end_shot_id ? `Shot ${shots.findIndex((s) => s.id === current.end_shot_id) + 1}` : "—");

  return (
    <ToolSplitLayout
      left={
        <div className="mx-auto max-w-4xl space-y-4" data-testid="level-page-session">
          <PrintReportShell
            toolName="Level Check"
            sessionName={current.name}
            passFail={
              !current.reference_ready ? null
              : shots.length === 0 ? null
              : Math.abs(current.max_z_delta_mm || 0) > current.tolerance_mm ? "FAIL" : "PASS"
            }
            headline={
              current.reference_ready
                ? `Worst Δz ${(current.max_z_delta_mm ?? 0).toFixed(1)} mm · tol ±${current.tolerance_mm} mm · ${shots.length} shots`
                : "Axis not set — no results yet"
            }
            surveyor={user?.username || user?.display_name || "—"}
          >
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B939A] no-print">Level · {current.name}</div>
              <h2
                className="text-2xl font-bold uppercase tracking-tight text-white no-print"
                style={{ fontFamily: "Chakra Petch, sans-serif" }}
              >
                {headline}
              </h2>
            </div>
            {current.reference_ready && shots.length > 0 && (
              <PrintReportButton testid="level-print-btn" />
            )}
          </div>

          {/* Axis + slope summary */}
          {current.reference_ready && !isFlat && (
            <div className="grid grid-cols-2 gap-3 rounded-md border border-[#009E8E]/30 bg-[#009E8E]/5 p-3 md:grid-cols-4">
              <Stat label="Axis length" value={`${(current.direction_length_m * 1000).toFixed(1)} mm`} />
              <Stat label="Axis bearing" value={`${current.direction_bearing_deg.toFixed(1)}°`} />
              <Stat
                label="Design slope"
                value={
                  current.slope_display_unit === "deg"
                    ? `${slopeDegFromMMperM(current.design_slope_mm_per_m).toFixed(3)}°`
                    : `${current.design_slope_mm_per_m.toFixed(2)} mm/m`
                }
              />
              <Stat
                label="Worst Δz"
                value={(() => {
                  const w = Math.max(0, ...shots.filter((s) => s.role === "check").map((s) => Math.abs(s.z_delta_mm || 0)));
                  return `${w.toFixed(1)} mm`;
                })()}
                tone={(() => {
                  const w = Math.max(0, ...shots.filter((s) => s.role === "check").map((s) => Math.abs(s.z_delta_mm || 0)));
                  return w > current.tolerance_mm ? "fail" : w > 0 ? "ok" : undefined;
                })()}
              />
            </div>
          )}

          {/* Flat-surface summary */}
          {current.reference_ready && isFlat && (
            <div className="grid grid-cols-2 gap-3 rounded-md border border-[#009E8E]/30 bg-[#009E8E]/5 p-3 md:grid-cols-4">
              <Stat
                label="Mean Z"
                value={`${current.mean_z_m != null ? (current.mean_z_m * 1000).toFixed(1) : "—"} mm`}
              />
              <Stat
                label="Range"
                value={`${current.range_mm?.toFixed(1) ?? "—"} mm`}
                tone={current.range_mm != null && current.range_mm > current.tolerance_mm * 2 ? "fail" : "ok"}
              />
              <Stat
                label="Worst high"
                value={(() => {
                  const hi = Math.max(0, ...shots.map((s) => s.z_delta_mm || 0));
                  return `+${hi.toFixed(1)} mm`;
                })()}
              />
              <Stat
                label="Worst low"
                value={(() => {
                  const lo = Math.min(0, ...shots.map((s) => s.z_delta_mm || 0));
                  return `${lo.toFixed(1)} mm`;
                })()}
              />
            </div>
          )}

          {/* Elevation profile */}
          <ElevationView session={current} />

          {/* Shot table */}
          <div className="overflow-hidden rounded-md border border-[#2A2E39] bg-[#14161A]">
            <table className="w-full text-[11px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              <thead>
                <tr className="border-b border-[#2A2E39] bg-[#0A0B0E] text-[#8B939A]">
                  <th className="p-2 text-left uppercase tracking-widest">#</th>
                  <th className="p-2 text-left uppercase tracking-widest">Label</th>
                  {!isFlat && <th className="p-2 text-right uppercase tracking-widest">Along (m)</th>}
                  <th className="p-2 text-right uppercase tracking-widest">
                    {isFlat ? "Δz from mean" : "Δz actual"}
                  </th>
                  {!isFlat && <th className="p-2 text-right uppercase tracking-widest">Δz design</th>}
                  <th className="p-2 text-right uppercase tracking-widest">
                    {isFlat ? "Deviation (mm)" : "Δz err (mm)"}
                  </th>
                  <th className="p-2 text-center uppercase tracking-widest">Role</th>
                  <th className="p-2 uppercase tracking-widest"></th>
                </tr>
              </thead>
              <tbody className="text-[#B5BEC5]">
                {shots.length === 0 ? (
                  <tr>
                    <td colSpan={isFlat ? 6 : 8} className="p-6 text-center text-[#5A6065]">
                      No shots yet — hit &quot;Take Shot&quot; on the right to begin.
                    </td>
                  </tr>
                ) : (
                  shots.map((s, i) => (
                    <ShotRow
                      key={s.id}
                      shot={s}
                      index={i}
                      onDelete={deleteShot}
                      onSetStart={(id) => setAnchor("start", id)}
                      onSetEnd={(id) => setAnchor("end", id)}
                      isStart={s.id === current.start_shot_id}
                      isEnd={s.id === current.end_shot_id}
                      isFlat={isFlat}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
          </PrintReportShell>
        </div>
      }
      right={
        <>
          <div className="flex items-center gap-2">
            <button
              data-testid="level-back-session"
              onClick={() => setCurrent(null)}
              className="flex-1 rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white"
            >
              ← Sessions
            </button>
            <a
              data-testid="level-download-csv"
              href={levelApi.csvUrl(current.id)}
              className="flex items-center gap-1 rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white"
            >
              <Download size={12} weight="bold" /> CSV
            </a>
          </div>

          <button
            data-testid="level-take-shot"
            disabled={measuring}
            onClick={takeShot}
            className={`flex min-h-[80px] items-center justify-center gap-2 rounded-lg border-2 px-4 py-4 text-base font-bold uppercase tracking-widest transition-all ${
              measuring
                ? "cursor-wait border-[#5A6065] bg-[#14161A] text-[#5A6065]"
                : "border-[#009E8E] bg-[#009E8E]/10 text-[#009E8E] hover:bg-[#009E8E]/20"
            }`}
            style={{ fontFamily: "Chakra Petch, sans-serif" }}
          >
            <Crosshair size={22} weight="bold" />
            {measuring ? "Measuring…" : `Take shot #${nextIdx + 1}`}
          </button>

          <AimPad tool="level" sessionId={current.id} />

          {/* Anchor status */}
          <div className="rounded-md border border-[#2A2E39] bg-[#0A0B0E] p-3">
            <div className="mb-2 text-[10px] uppercase tracking-widest text-[#8B939A]">Beam axis</div>
            <div className="grid grid-cols-2 gap-2 text-[10px]">
              <div>
                <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-[#8DC63F]">
                  <Flag size={10} weight="bold" /> Start
                </div>
                <div className="mt-0.5 truncate font-mono text-sm text-white">{startLabel}</div>
              </div>
              <div>
                <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-[#F5A623]">
                  <MapPin size={10} weight="bold" /> End
                </div>
                <div className="mt-0.5 truncate font-mono text-sm text-white">{endLabel}</div>
              </div>
            </div>
            <div className="mt-2 text-[9px] uppercase tracking-widest text-[#5A6065]">
              Tag anchors from the shot table on the left.
            </div>
          </div>

          <div className="grid grid-cols-[1fr,88px] gap-2">
            <label className="block">
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[#8B939A]">
                Shot label
              </span>
              <input
                data-testid="level-label"
                value={labelInput}
                onChange={(e) => setLabelInput(e.target.value)}
                placeholder="mid-span · col-B"
                className="w-full rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white placeholder:text-[#4A5054] focus:border-[#009E8E] focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[#8B939A]">
                ± Tol mm
              </span>
              <input
                data-testid="level-tol-input"
                type="number" step="0.1"
                value={tolInput}
                onChange={(e) => setTolInput(e.target.value)}
                onBlur={saveTolerance}
                className="w-full rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-2 py-2 font-mono text-sm text-white focus:border-[#009E8E] focus:outline-none"
              />
            </label>
          </div>

          <SlopeInput
            mmPerM={current.design_slope_mm_per_m}
            unit={current.slope_display_unit || "mm_per_m"}
            onCommit={saveSlope}
          />

          {current.warning && (
            <div className="rounded-sm border border-[#FF9F0A]/60 bg-[#FF9F0A]/10 px-3 py-2 text-[10px] uppercase tracking-widest text-[#FFB84D]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              ⓘ {current.warning}
            </div>
          )}
        </>
      }
    />
  );
}
